import { ReactNode, createContext, useCallback, useContext, useMemo } from 'react';

import { FullProject } from 'entities';

import { FormControlHook, useFormControl } from 'hooks';
import { useUrlStateV2 } from 'hooks/urlState';

import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { TaskOptionOverlayProps } from '../createTaskOptions';

export interface RecruitmentModalContext
  extends Pick<TaskOptionOverlayProps, 'task' | 'close' | 'updateTask'> {
  form: FormControlHook<{
    sampleId: string;
    country: string;
    gender: string;
    selIncome: string;
    region: string;
    requestComment: string;
    completesNecessary: string;
    ageRange: [number, number];
  }>;

  project: FullProject;
  sampleIdOnQuery?: string;
}

const context = createContext({} as RecruitmentModalContext);

type ProviderProps = Pick<TaskOptionOverlayProps, 'task' | 'close' | 'updateTask'> & {
  children: ReactNode;
};

export function RecruitmentModalProvider({
  children,
  task,
  close,
  updateTask,
}: ProviderProps): JSX.Element {
  const { project } = useProjectProfile();

  const [{ lastSampleId }, updateUrlParams] = useUrlStateV2<{ lastSampleId: string }>();

  const closeModal = useCallback((): void => {
    updateUrlParams({ lastSampleId: null });
    close();
  }, [close, updateUrlParams]);

  const form = useFormControl({
    sampleId: '',
    country: '',
    gender: '',
    selIncome: '',
    region: '',
    requestComment: '',
    completesNecessary: '',

    ageRange: [13, 99] as [number, number],
  });

  const ctxData = useMemo(
    () => ({
      form,
      close: closeModal,
      project,
      task,
      sampleIdOnQuery: lastSampleId,
      updateTask,
    }),
    [form, closeModal, project, task, lastSampleId, updateTask],
  );

  return <context.Provider value={ctxData}>{children}</context.Provider>;
}

export function useRequestRecruitmentModal(): RecruitmentModalContext {
  return useContext(context);
}
